// ** React Import
import { useId, useState } from 'react'

// ** Next Import
import Link from 'next/link'

// ** MUI Imports
import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { styled } from '@mui/material/styles'

// ** Custom Icon Import
import Icon from 'src/@core/components/icon'

import { anioActual } from 'src/utils/fechas'

/*
  prefetch={false} por el mismo motivo que en card-menu y VerticalNavLink: el pie
  esta en todas las paginas y sus seis enlaces legales precargaban su chunk en
  cuanto entraban en viewport. Son ~20 KB que en movil casi nadie llega a usar.
*/
const LinkStyled = styled(props => <Link prefetch={false} {...props} />)(({ theme }) => ({
  textDecoration: 'none',
  color: theme.palette.primary.main
}))

const EnlacesLegales = () => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '1rem' }}>
    <LinkStyled href='/about/'>About</LinkStyled>
    <LinkStyled href='/contact/'>Contact</LinkStyled>
    <LinkStyled href='/guides/'>Guides</LinkStyled>
    <Divider orientation='vertical' flexItem />
    <LinkStyled href='/privacy/'>Privacy Policy</LinkStyled>
    <LinkStyled href='/terms/'>Terms of Service</LinkStyled>
    <LinkStyled href='/disclaimer/'>Disclaimer</LinkStyled>
  </Box>
)

const Creditos = () => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '1rem' }}>
    <Box>
      <LinkStyled target='_blank' href='https://onadev.net'>
        onaDev
      </LinkStyled>
      {` © ${anioActual()}`}
    </Box>
    <LinkStyled target='_blank' href='https://www.paypal.com/paypalme/oNaare'>
      Donate
    </LinkStyled>
    <Typography variant='caption' color='text.secondary'>
      WAO Tools is not affiliated with War and Order or its publishers.
    </Typography>
  </Box>
)

/**
 * Pie del sitio.
 *
 * En movil va plegado. El pie es `position: sticky` con `bottom: 0`, asi que lo que
 * ocupa lo ocupa siempre: medido en un iPhone 14 (390x844) eran 220 px de alto, el
 * 26% de la pantalla, permanentes, encima de una calculadora que ya compite por
 * espacio con la barra de la app. Seis enlaces y la linea de no-afiliacion no
 * entraban en una linea y se repartian en cuatro.
 *
 * Plegado deja una sola linea con lo que si tiene que estar a la vista sin tocar
 * nada: la politica de privacidad —que la politica de AdSense exige accesible— y el
 * credito. El resto se abre con el boton.
 *
 * El contenido plegado sigue en el DOM: <Collapse> no desmonta a sus hijos, asi que
 * los enlaces internos del pie se siguen rastreando. Esconderlos por completo en
 * movil habria roto el enlazado interno de /about, /contact y /guides justo en el
 * dispositivo desde el que llega la mayoria del trafico.
 */
const FooterContent = () => {
  /*
    El corte va en md (<900) y no en sm (<600). Medido contra produccion: a 599 px
    el pie son 44 px con su boton, y a 600 px justos saltaba a 160 px —el 19% de la
    pantalla— sin boton para cerrarlo. La tableta en vertical se quedaba con lo peor
    de las dos: ancho de telefono para repartir seis enlaces, y sin pliegue.
  */
  const esEstrecho = useMediaQuery(theme => theme.breakpoints.down('md'))
  const [abierto, setAbierto] = useState(false)
  const idPanel = useId()

  if (!esEstrecho) {
    return (
      <Box sx={{ width: '100%' }}>
        <Box sx={{ mb: 1 }}>
          <EnlacesLegales />
        </Box>
        <Creditos />
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Linea siempre visible: privacidad, credito y el boton para abrir el resto */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          minHeight: 32
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
          <LinkStyled href='/privacy/' style={{ fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>
            Privacy
          </LinkStyled>
          <Typography variant='caption' color='text.secondary' noWrap>
            {`onaDev © ${anioActual()}`}
          </Typography>
        </Box>

        <IconButton
          size='small'
          onClick={() => setAbierto(a => !a)}
          aria-expanded={abierto}
          aria-controls={idPanel}
          aria-label={abierto ? 'Hide footer links' : 'Show footer links'}
          /*
            El indicador de foco va explicito. El tema tiene disableRipple en true y
            MUI cuelga el suyo del ripple, asi que el boton recibia la clase
            .Mui-focusVisible y no pintaba absolutamente nada: con teclado no habia
            forma de saber donde estabas parado.
          */
          sx={{
            flexShrink: 0,
            '&.Mui-focusVisible': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: 2
            }
          }}
        >
          <Icon icon={abierto ? 'mdi:chevron-down' : 'mdi:chevron-up'} fontSize={20} />
        </IconButton>
      </Box>

      <Collapse in={abierto} id={idPanel}>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <EnlacesLegales />
          <Creditos />
        </Box>
      </Collapse>
    </Box>
  )
}

export default FooterContent
